import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {CATEGORIES_DROPDOWN_VALUES} from '../constants/constants';

import Button from 'eventbrite_design_system/button/Button';
import Icon from 'eventbrite_design_system/icon/Icon';
import SelectField from 'eventbrite_design_system/inputField/SelectField';
import ArrowRightChunkySvg from 'eventbrite_design_system/iconography/icons/ArrowRightChunky';
import LoadingOverlay from 'eventbrite_design_system/loadingOverlay/LoadingOverlay';
import {constants as destinationConstants} from '@eb/destination-utils';
import {setMark} from '@eb/performance-monitor';
import interpolate from 'js-utils/interpolate';

import {DateInput} from './DateComponent/DateInput';
import ConnectedLocationSearchAutocomplete from '../containers/ConnectedLocationSearchAutocomplete';

import {gettext} from '../translations';

import './MadLibs.scss';

const {LOCATION_DIALOG_CONTENT} = destinationConstants;
const LOCATION_AUTOCOMPLETE_PROPS = {
    borderType: 'none',
    id: 'madlibs-locationPicker',
    name: 'madlibs-locationPicker',
    hideOutline: true,
    isLimitedSearch: true,
    preventInputValuePrefill: true,
    shouldRemoveConflictiveAria: true,
};


export default class MadLibs extends PureComponent {
    static propTypes = {
        handleUseCurrentLocation: PropTypes.func.isRequired,
        handleMadLibsSearch: PropTypes.func.isRequired,
        locationDenied: PropTypes.bool,
        shouldShowSearchAnimation: PropTypes.bool,
        showOverlay: PropTypes.func,
        hideOverlay: PropTypes.func,
        isMobile: PropTypes.bool,
    }

    state = {
        tags: '',
        dates: '',
        isSearching: false,
        sortedCategories: [{display: gettext('Anything'), value: ''}].concat(CATEGORIES_DROPDOWN_VALUES),
    };

    componentDidMount() {
        setMark('feed-madlibs-ready');
    }

    componentDidUpdate(prevProps) {
        const {
            locationDenied,
            showOverlay,
        } = this.props;

        if (locationDenied !== prevProps.locationDenied && showOverlay) {
            showOverlay(
                'dialog',
                LOCATION_DIALOG_CONTENT
            );
        }
    }

    _handleSubmit = (e) => {
        e.preventDefault();
        const {
            handleMadLibsSearch,
        } = this.props;

        const {tags, dates} = this.state;

        this.setState({
            isSearching: true,
        });

        handleMadLibsSearch(tags, dates);
    }

    _onUseCurrentLocation = () => {
        const {
            handleUseCurrentLocation,
            showOverlay,
            locationDenied,
        } = this.props;

        if (locationDenied) {
            return showOverlay(
                'dialog',
                LOCATION_DIALOG_CONTENT,
            );
        }

        return handleUseCurrentLocation();
    }

    _handleChangeValue = (key, value) => {
        this.setState({[key]: value});
    }

    _handleTagChange = (value) => {
        this.setState({tags: value});
    }

    render() {
        const {dates, isSearching, sortedCategories} = this.state;
        const {shouldShowSearchAnimation} = this.props;

        const formButtonClasses = classNames(
            'header-search__form-button',
            {'header-search__form-button--animated': shouldShowSearchAnimation},
        );
        const loadingOverlayContainerClasses = classNames(
            {
                'header-search__overlay': isSearching,
            },
        );

        let labelComponent = null;

        if (shouldShowSearchAnimation) {
            labelComponent = (
                <div className="header-search__btn-content eds-text-bm eds-text-weight--heavy">
                    {gettext('Search')}
                </div>
            );
        }

        return (
            <section className="feed-header__madlibs eds-bg-color--white">
                <div className="header-search__content" data-spec="header-search__content">

                    <div className={loadingOverlayContainerClasses}>
                        <LoadingOverlay size="large-chunky" isShown={isSearching} />
                    </div>

                    <form
                        onSubmit={this._handleSubmit}
                        autoComplete="none"
                        className="header-search__form eds-align--center"
                    >
                        <div className="header-search__form-content eds-align--center">
                            <div className="header-search__form-item header-search__form-date-item">
                                <div
                                    className="eds-text-bs eds-text-color--ui-700"
                                    data-spec="header-search__content-date-input-text"
                                >
                                    {gettext('I want to go out')}
                                </div>
                                <DateInput
                                    id="madlibs-date-selector"
                                    label={interpolate(gettext('Choose your ideal date'))}
                                    onChangeValue={this._handleChangeValue}
                                    dates={dates}
                                    // trackGAEvent={trackGAEvent}
                                />
                            </div>
                            <div className="header-search__form-item header-search__form-location-item eds-l-pad-vert-5">
                                <div
                                    className="eds-text-bs eds-text-color--ui-700"
                                    data-spec="header-search__content-location-input-text"
                                >
                                    {gettext('In')}
                                </div>
                                <ConnectedLocationSearchAutocomplete
                                    shouldDispatchSearchActions={true}
                                    handleUseCurrentLocation={this._onUseCurrentLocation}
                                    autocompleteProps={LOCATION_AUTOCOMPLETE_PROPS}
                                />
                            </div>
                            <div className="header-search__form-item header-search__form-mood-item">
                                <div
                                    className="eds-text-bs eds-text-color--ui-700"
                                    data-spec="header-search__content-mood-input-text"
                                >
                                    {gettext('And I\'m in the mood for')}
                                </div>
                                <SelectField
                                    id="madlibs-mood-selector"
                                    label={interpolate(gettext('Select your mood from the list'))}
                                    onChange={this._handleTagChange}
                                    values={sortedCategories}
                                    style="basic"
                                    borderType="none"
                                />
                            </div>
                            <div className={formButtonClasses}>
                                <div
                                    className="eds-align--center eds-text-bl"
                                    data-spec="header-search__content-submit-button"
                                >
                                    <Button
                                        aria-label={gettext('Search events with your preferences')}
                                        style="fill"
                                        disabled={false}
                                        size="responsive"
                                        type="submit"
                                    >
                                        {labelComponent}
                                        <Icon type={<ArrowRightChunkySvg />} />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </section>
        );
    }
}
